import { LOGIN_UNIQUE_ID_STORAGE_KEY } from "@shared/common/constants";
import {
  ApiGeneralDataResponse,
  ApiResponse,
  ApiTags,
  ChangePasswordRequest,
  ConnectToTelegramRequest,
  ConnectDiscordOAuthRequest,
  DeleteCornixAccountConfirmationEmailRequest,
  DisableTwoFARequest,
  ForgotPasswordRequest,
  GenerateQRCodeRequest,
  GenerateQRCodeResponse,
  GetCaptchaRequest,
  GetCaptchaResponse,
  GetJobResultResponse,
  LoginRequest,
  LoginResponse,
  LoginResponseWithUserId,
  OAuthUserRequest,
  Platform,
  ReferralRequest,
  RefreshTokenRequest,
  RefreshTokenResponse,
  RegisterRequest,
  ResendRegistrationEmailRequest,
  ResetPasswordRequest,
  SendConnectToTelegramAuthCodeRequest,
  StoreTwoFARequest,
  UserBasicDetails,
  UserProperties,
  ValidateCaptchaRequest,
  ValidateCaptchaResponse,
  VerifyPersonalDetailsRequest,
  MarkVisitRequest,
  ValidateWebAppInitDataResponse,
  ValidateWebAppInitDataRequest,
  ChangeUserFullNameRequest,
  GetLoginAuthUrlRequest,
  OAuthAuthorizeRequest,
  OAuthAuthorizeResponse,
} from "@shared/types";
import { EndpointBuilder, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { getIsDemoQueryArgs } from "@shared/common/utils";
import { sharedLib } from "@shared/services/shared.service";

const updateTwoFAStatus = (isEnabled: boolean) => {
  return sharedLib.slices.authentication.actions.updateUser({
    key: "has_two_factor_auth",
    value: isEnabled,
  });
};

const addUniqueLoginIdToArgs = async (args: any): Promise<any> => {
  let loginUniqueId: string | undefined | null;
  try {
    loginUniqueId = sharedLib.storageService.getValue(
      LOGIN_UNIQUE_ID_STORAGE_KEY
    );
  } catch {}
  if (loginUniqueId) {
    return {
      ...args,
      login_unique_id: loginUniqueId,
    };
  }
  return args;
};

const getAuthQueryFn =
  (url: string, transformResponse?: any) =>
  async (args: any, _: any, __: any, baseQuery: any) => {
    const response: { data: GetJobResultResponse } | FetchBaseQueryError =
      await baseQuery({
        url,
        method: "POST",
        body: await addUniqueLoginIdToArgs(args),
      });
    if (response && "error" in response) {
      return {
        error: response.error,
      } as any;
    } else {
      const parsedData = transformResponse
        ? transformResponse(response.data, args)
        : response.data;
      return { data: parsedData as Response };
    }
  };

export const authEndpoints = {
  endpoints: (build: EndpointBuilder<any, any, any>) => ({
    login: build.mutation<LoginResponse, LoginRequest>({
      queryFn: getAuthQueryFn("login/"),
    }),
    refreshToken: build.mutation<RefreshTokenResponse, RefreshTokenRequest>({
      query: (args) => ({
        url: "refresh_token/",
        method: "POST",
        body: args,
      }),
    }),
    oauthAuthorize: build.mutation<
      OAuthAuthorizeResponse,
      OAuthAuthorizeRequest
    >({
      query: ({ url, decision }) => ({
        url,
        method: decision ? "POST" : "GET",
        body: decision ? { decision } : undefined,
      }),
    }),
    logout: build.mutation<void, { refresh_token?: string }>({
      query: (args) => ({
        url: "logout/",
        method: "POST",
        body: args,
      }),
    }),
    register: build.mutation<LoginResponse, RegisterRequest>({
      queryFn: getAuthQueryFn("register/"),
    }),
    getUser: build.query<UserProperties, { is_demo_query?: boolean } | void>({
      query: (args) => ({
        url: "get_user/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: { data: UserProperties }) => response.data,
      async onQueryStarted(args, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (!sharedLib.selectors.authentication.selectToken(getState()))
            return;
          dispatch(
            sharedLib.slices.authentication.actions.setUser({
              isDemo: getIsDemoQueryArgs(args),
              user: data,
            })
          );
        } catch {}
      },
      providesTags: () => [{ type: ApiTags.USER, id: "LIST" }],
      keepUnusedDataFor: 0,
    }),
    getUserLite: build.query<Partial<UserProperties>, void>({
      query: (args) => ({
        url: "get_user_lite/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: ApiResponse<UserProperties>) =>
        response.data,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            sharedLib.slices.authentication.actions.updateUserLite(data)
          );
        } catch {}
      },
    }),
    getBasicDetailsFromUuid: build.query<
      UserBasicDetails,
      { user_short_uuid: string }
    >({
      query: (args) => ({
        url: "get_basic_details_from_uuid/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: { data: UserBasicDetails }) =>
        response.data,
    }),
    verifyConfirmEmailCode: build.mutation<
      LoginResponseWithUserId,
      { auth_code: number }
    >({
      query: (args) => ({
        url: "verify_confirm_email_code/",
        method: "POST",
        body: args,
      }),
    }),
    verifyConfirmEmailCodeByUUID: build.mutation<
      LoginResponseWithUserId,
      { auth_code: number; email_validator_uuid: string }
    >({
      query: (args) => ({
        url: "verify_confirm_email_code_by_uuid/",
        method: "POST",
        body: args,
      }),
    }),
    resendRegistrationEmail: build.mutation<
      void,
      ResendRegistrationEmailRequest
    >({
      queryFn: getAuthQueryFn("resend_registration_email/"),
    }),
    connectToTelegram: build.mutation<void, ConnectToTelegramRequest>({
      query: (args) => ({
        url: "connect_to_telegram/",
        method: "POST",
        body: args,
      }),
    }),
    connectDiscordOAuth: build.mutation<void, ConnectDiscordOAuthRequest>({
      query: (args) => ({
        url: "connect_discord_oauth/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: [{ type: ApiTags.USER, id: "LIST" }],
    }),
    getUuidDeeplink: build.query<string, { platform: Platform }>({
      query: (args) => ({
        url: "get_uuid_deeplink/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<string>>
      ) => {
        return response.data.data;
      },
    }),
    verifyPersonalDetails: build.query<void, VerifyPersonalDetailsRequest>({
      query: (args) => ({
        url: "verify_personal_details/",
        method: "POST",
        body: args,
      }),
    }),
    sendConnectToTelegramAuthCode: build.mutation<
      void,
      SendConnectToTelegramAuthCodeRequest
    >({
      query: (args) => ({
        url: "send_connect_to_telegram_auth_code/",
        method: "POST",
        body: args,
      }),
    }),
    sendConnectToTelegramAuthCodeFromUuid: build.mutation<
      void,
      {
        uuid: string;
        platform: Platform;
      }
    >({
      query: (args) => ({
        url: "send_connect_to_telegram_auth_code_from_uuid/",
        method: "POST",
        body: args,
      }),
    }),
    finishOnboarding: build.mutation<void, void>({
      query: (args) => ({
        url: "finish_onboarding/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: [{ type: ApiTags.USER, id: "LIST" }],
    }),
    resetPassword: build.mutation<{}, ResetPasswordRequest>({
      query: (resetPasswordArgs) => ({
        url: "reset_password_inapp/",
        method: "POST",
        body: resetPasswordArgs,
      }),
    }),
    changePassword: build.mutation<{}, ChangePasswordRequest>({
      query: (args) => ({
        url: "change_password/",
        method: "POST",
        body: args,
      }),
    }),
    setPersonalDetails: build.mutation<void, ChangeUserFullNameRequest>({
      query: (args) => ({
        url: "set_personal_details/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: [{ type: ApiTags.USER, id: "LIST" }],
    }),
    forgotPassword: build.mutation<{}, ForgotPasswordRequest>({
      queryFn: getAuthQueryFn("forgot_password/"),
    }),
    generateQRCode: build.query<GenerateQRCodeResponse, GenerateQRCodeRequest>({
      query: (args) => ({
        url: "generate_qr_code/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: ApiResponse<GenerateQRCodeResponse>) =>
        response.data,
    }),
    storeTwoFA: build.mutation<ApiResponse<void>, StoreTwoFARequest>({
      query: (args) => ({
        url: "store_two_factor_auth/",
        method: "POST",
        body: args,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(updateTwoFAStatus(true));
        } catch {}
      },
      invalidatesTags: [{ type: ApiTags.USER, id: "LIST" }],
    }),
    disableTwoFA: build.mutation<ApiResponse<void>, DisableTwoFARequest>({
      query: (args) => ({
        url: "disable_two_factor_auth/",
        method: "POST",
        body: args,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(updateTwoFAStatus(false));
        } catch {}
      },
      invalidatesTags: [{ type: ApiTags.USER, id: "LIST" }],
    }),
    getCaptcha: build.query<GetCaptchaResponse, GetCaptchaRequest>({
      queryFn: getAuthQueryFn(
        "get_captcha/",
        (response: { data: { data: GetCaptchaResponse } }) => response.data.data
      ),
      keepUnusedDataFor: 0,
    }),
    validateCaptcha: build.query<
      ValidateCaptchaResponse,
      ValidateCaptchaRequest
    >({
      queryFn: getAuthQueryFn(
        "validate_captcha/",
        (response: { data: { data: ValidateCaptchaResponse } }) => {
          return response.data.data;
        }
      ),
    }),
    checkReferral: build.query<number, ReferralRequest>({
      query: (args) => ({
        url: "check_referral/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: { data: { data: number } }) => {
        return response.data.data;
      },
    }),
    referralClicked: build.mutation<void, ReferralRequest>({
      query: (args) => ({
        url: "referral_clicked/",
        method: "POST",
        body: args,
      }),
    }),
    deleteCornixAccountConfirmationEmail: build.mutation<
      void,
      DeleteCornixAccountConfirmationEmailRequest
    >({
      query: (args) => ({
        url: "delete_cornix_account_confirmation_email/",
        method: "POST",
        body: args,
      }),
    }),
    checkVersion: build.query<any, { version: string }>({
      query: (args) => ({
        url: "check_version/",
        method: "POST",
        body: args,
      }),
    }),
    getLoginAuthUrl: build.query<string, GetLoginAuthUrlRequest>({
      query: (args) => ({
        url: "get_login_auth_url/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<string>>
      ) => response.data.data,
    }),
    oauthUser: build.mutation<LoginResponseWithUserId, OAuthUserRequest>({
      queryFn: getAuthQueryFn("oauth_user/"),
    }),
    validateWebAppInitData: build.mutation<
      ValidateWebAppInitDataResponse,
      ValidateWebAppInitDataRequest
    >({
      query: (args) => ({
        url: "validate_web_app_init_data/",
        method: "POST",
        body: args,
      }),
    }),
    getWebAppInitData: build.query<any, void>({
      query: () => ({
        url: "get_web_app_init_data/",
        method: "POST",
      }),
      transformResponse: (response: ApiResponse<ApiGeneralDataResponse<any>>) =>
        response.data.data,
    }),
    markVisit: build.mutation<void, MarkVisitRequest>({
      query: (args) => ({
        url: "mark_visit/",
        method: "POST",
        body: args,
      }),
    }),
  }),
  overrideExisting: false,
};
